/* ============================================================================
   RYVT cart module — drawer state, mutation sequencing, and merchandising.
   <ryvt-cart-drawer> extends RyvtDrawer (ryvt-runtime.js loads first).
   - Opens itself on ryvt:cart:change with action === 'add' (stays open, R1).
   - SR-10: meter + drawer title + progressbar updated IN PLACE from the parsed
     Section Rendering response; morphs (runtime) only touch
     [data-ryvt-cart-lines] and [data-ryvt-cart-footer].
   - Merchandising selection on every cart change: choose a missing outfit role,
     then an accessory; within each role prefer the least expensive item that
     closes the remaining shipping gap. Never fall back across categories.
   - Qty changes debounced 300ms; Remove leaves a ~5s inline undo row; the
     2000ms Added revert and the undo timer are the only sanctioned timers.
   - Zero client-side money math: JS moves server-rendered text and compares
     integer cents only. No new event names (SR-6 closed set).
   Method map: qStep/qSet qty stepper+debounce ·
   rm remove->undo row · unRm undo re-add · upAdd upsell add (C8 feedback) ·
   changed ryvt:cart:change handler · meter SR-10 in-place sync · upsells R8
   selection · refocus/say/fail focus + aria-live + role=alert helpers.
   ========================================================================== */
(() => {
  const R = window.RYVT;
  if (!R || !R.RyvtDrawer || customElements.get('ryvt-cart-drawer')) return;

  const I = (v) => parseInt(v, 10) || 0;
  const A = (el, n) => el.getAttribute(n);
  const D = (n) => '[data-ryvt-' + n + ']';
  const q = (el, s) => el.querySelector(s);
  const EV = 'ryvt:cart:change';
  const KEY = 'data-line-key';
  const VID = 'data-variant-id';
  const MS = 'data-ryvt-meter-state';
  const HID = 'aria-hidden';
  const TI = D('cart-title');
  /* The heading is Title voice + a mono count span. Retexting the heading would
     collapse that span, so only the count node is ever rewritten. */
  const TC = D('cart-title-count');
  const CLICKS = [
    [D('qty-plus') + ',' + D('qty-minus'), 'qStep'],
    [D('remove'), 'rm'],
    [D('undo'), 'unRm'],
    [D('upsell-add'), 'upAdd'],
    [D('upsell-choose'), 'upOpen'],
  ];

  const truthy = (el, name) => A(el, name) === 'true';

  function ranked(rows, gap) {
    return rows.slice().sort((a, b) => {
      const ap = I(A(a, 'data-price'));
      const bp = I(A(b, 'data-price'));
      const ac = gap > 0 && ap >= gap;
      const bc = gap > 0 && bp >= gap;
      if (ac !== bc) return ac ? -1 : 1;
      return ap - bp;
    });
  }

  function chooseMerch(region, data, limit) {
    if (!region || !data || limit < 1) return [];
    const inCart = (A(data, 'data-products') || '').split(',').filter(Boolean);
    const rows = [...region.querySelectorAll(D('upsell-row'))].filter(
      (row) => inCart.indexOf(A(row, 'data-product-id')) === -1 && A(row, 'data-merch-role') !== 'none'
    );
    const gap = Math.max(0, I(A(data, 'data-threshold')) - I(A(data, 'data-total')));
    const hasTop = truthy(data, 'data-has-top');
    const hasBottom = truthy(data, 'data-has-bottom');
    const hasAccessory = truthy(data, 'data-has-accessory');
    const role = (name) => ranked(rows.filter((row) => A(row, 'data-merch-role') === name), gap);
    const chosen = [];
    const take = (pool) => {
      const next = pool.find((row) => chosen.indexOf(row) === -1);
      if (next && chosen.length < limit) chosen.push(next);
    };

    if (hasTop && !hasBottom) take(role('bottom'));
    else if (hasBottom && !hasTop) take(role('top'));
    else if (!hasTop && !hasBottom && hasAccessory) {
      take(role('top'));
      take(role('bottom'));
    }

    if ((hasTop || hasBottom) && !hasAccessory) take(role('accessory'));
    return chosen.slice(0, limit);
  }

  class RyvtCartDrawer extends R.RyvtDrawer {
    connectedCallback() {
      super.connectedCallback();
      this._t = {};
      this._quantityJobs = {};
      this._queue = Promise.resolve();
      this._issued = 0;
      this._pendingMutations = 0;
      this._failureVersion = 0;
      this._mutationError = null;
      this._needsSync = false;
      this._checkoutPending = false;
      this._err = (e) => this.fail(e);
      this._vh = matchMedia('(max-height: 700px)');
      this._sync = () => this.upsells();
      this._vh.addEventListener('change', this._sync);
      this._chg = (d) => this.changed(d);
      R.on(EV, this._chg);
      this._clk = (e) => {
        for (const [sel, fn] of CLICKS) {
          const t = e.target.closest(sel);
          if (t) return this[fn](t);
        }
      };
      this.addEventListener('click', this._clk);
      this._fld = (e) => {
        const input = e.target.closest(D('qty-input'));
        if (input) return this.qSet(input);
        const opt = e.target.closest(D('upsell-opt'));
        if (opt) this.upResolve(opt);
      };
      this.addEventListener('change', this._fld);
      this._submit = (e) => this.checkout(e);
      this.addEventListener('submit', this._submit);
      this._pageShow = () => {
        this._checkoutPending = false;
        this.syncCheckout();
      };
      window.addEventListener('pageshow', this._pageShow);
      this.upsells();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this._vh.removeEventListener('change', this._sync);
      R.off(EV, this._chg);
      this.removeEventListener('click', this._clk);
      this.removeEventListener('change', this._fld);
      this.removeEventListener('submit', this._submit);
      window.removeEventListener('pageshow', this._pageShow);
      for (const k in this._t) clearTimeout(this._t[k]);
      this._t = {};
      this._quantityJobs = {};
    }

    syncCheckout() {
      const busy = this._checkoutPending || this._pendingMutations > 0 || Object.keys(this._quantityJobs).length > 0;
      const button = q(this, 'button[name="checkout"]');
      if (!button) return;
      // Keep checkout reachable so activating it can flush a pending quantity.
      if (busy) {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
      }
    }

    async checkout(event) {
      if (!event.target.matches('.ryvt-cart__checkout-form')) return;
      if (this._allowCheckout) {
        this._allowCheckout = false;
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (this._checkoutPending) return;
      this._checkoutPending = true;
      const failureVersion = this._failureVersion;
      const pendingError = this._pendingMutations > 0 && this._mutationError;
      this.syncCheckout();
      try {
        // Finish the original submit event before requesting another native
        // submission. Browsers ignore requestSubmit while that event is firing,
        // even after awaiting an already-settled cart queue.
        await new Promise((resolve) => setTimeout(resolve, 0));
        // A shopper may keep editing while a request is in flight. Drain the
        // current jobs and queue again until every displayed change has settled.
        do {
          Object.values(this._quantityJobs).forEach((run) => run());
          await this._queue;
          if (pendingError || this._failureVersion !== failureVersion) throw this._mutationError;
          if (this._needsSync) {
            await this.restore();
            this._needsSync = false;
          }
        } while (Object.keys(this._quantityJobs).length || this._pendingMutations > 0);
        if (pendingError || this._failureVersion !== failureVersion) throw this._mutationError;
        // Section rendering replaces the original form while we wait.
        const form = q(this, '.ryvt-cart__checkout-form');
        const button = form && q(form, 'button[name="checkout"]');
        if (!form || !button) {
          this._checkoutPending = false;
          this.syncCheckout();
          return;
        }
        this._allowCheckout = true;
        form.requestSubmit(button);
        this._allowCheckout = false;
      } catch (error) {
        this._allowCheckout = false;
        this._checkoutPending = false;
        this.syncCheckout();
        this.fail(error);
      }
    }

    request(action, payload, ticket, source) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const body = Object.assign({}, payload, {
        sections: 'ryvt-cart-drawer,ryvt-header',
        sections_url: window.location.pathname,
      });
      return fetch(`${root}cart/${action}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            return Promise.reject({
              message: (data && (data.description || data.message)) || '',
            });
          }
          if (ticket === this._issued) {
            R.renderSections(data.sections || {});
            R.emit(EV, { cart: data, action });
            R.trackCartResult(action, payload, data, source);
          }
          return data;
        });
    }

    restore() {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: {},
          sections: 'ryvt-cart-drawer,ryvt-header',
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) return Promise.reject(data);
          R.renderSections(data.sections || {});
          R.emit(EV, { cart: data, action: 'sync' });
          return data;
        });
    }

    mutate(action, payload, source) {
      const ticket = ++this._issued;
      if (this._pendingMutations === 0) this._mutationError = null;
      this._pendingMutations += 1;
      this.syncCheckout();
      const task = this._queue
        .catch(() => {})
        .then(() => this.request(action, payload, ticket, source))
        .catch((error) => {
          this._failureVersion += 1;
          this._mutationError = error;
          this._needsSync = true;
          if (ticket !== this._issued) return Promise.reject({ stale: true, original: error });
          return this.restore()
            .then(() => { this._needsSync = false; })
            .catch(() => null)
            .then(() => Promise.reject(error));
        })
        .finally(() => {
          this._pendingMutations -= 1;
          this.syncCheckout();
        });
      this._queue = task.catch(() => {});
      return task;
    }

    qStep(btn) {
      const row = btn.closest(D('cart-line'));
      const input = row && q(row, D('qty-input'));
      if (!input) return;
      input.value = Math.max(0, I(input.value) + (btn.hasAttribute('data-ryvt-qty-plus') ? 1 : -1));
      this.qSet(input);
    }

    qSet(input) {
      const key = A(input, KEY);
      if (!key) return;
      const quantity = Math.max(0, I(input.value));
      clearTimeout(this._t[key]);
      const run = () => {
        clearTimeout(this._t[key]);
        delete this._t[key];
        delete this._quantityJobs[key];
        const row = input.closest(D('cart-line'));
        if (row) row.setAttribute('aria-busy', 'true');
        this.mutate('change', { id: key, quantity }, 'quantity').catch((error) => {
          if (error && error.stale) return;
          if (row && row.isConnected) row.removeAttribute('aria-busy');
          if (input.isConnected) input.value = A(input, 'data-server-quantity') || input.value;
          this.fail(error);
        });
      };
      this._quantityJobs[key] = run;
      this._t[key] = setTimeout(run, 300);
      this.syncCheckout();
    }

    rm(btn) {
      const row = btn.closest(D('cart-line'));
      const key = A(btn, KEY);
      if (!row || !key) return;
      clearTimeout(this._t[key]);
      delete this._t[key];
      delete this._quantityJobs[key];
      const index = [...row.parentElement.children].indexOf(row);
      const tpl = q(row, D('undo-template'));
      const frag = tpl && tpl.content ? tpl.content.cloneNode(true) : null;
      const said = A(btn, 'data-removed-text') || '';
      this.mutate('change', { id: key, quantity: 0 }, 'remove').then(() => {
        this.say(said);
        const undoRow = frag && frag.firstElementChild;
        const lines = q(this, D('cart-lines'));
        if (!undoRow || !lines) return;
        const list = q(lines, D('cart-list'));
        if (list) list.insertBefore(undoRow, list.children[index] || null);
        else lines.prepend(undoRow);
        requestAnimationFrame(() => undoRow.classList.add('is-in'));
        undoRow._ryvtTimer = setTimeout(() => {
          undoRow.remove();
          this.refocus();
        }, 5000);
        if (document.activeElement === document.body) {
          const u = q(undoRow, D('undo'));
          if (u) u.focus();
        }
      }, (error) => {
        if (!error || !error.stale) this.fail(error);
      });
    }

    unRm(btn) {
      const row = btn.closest(D('undo-row'));
      if (!row) return;
      clearTimeout(row._ryvtTimer);
      const id = I(A(row, VID));
      row.remove();
      this.refocus();
      if (id) {
        this.mutate('add', { items: [{ id, quantity: I(A(row, 'data-quantity')) || 1 }] }, 'undo').catch((error) => {
          if (!error || !error.stale) this.fail(error);
        });
      }
    }

    /* §7.5 Drawer Variant Sheet — upsells never navigate away. upOpen toggles
       the row's sheet; upResolve matches the chosen option chips against the
       server-rendered variant map and arms the add button with that variant's
       id and its SERVER-RENDERED price string (zero client money math). The
       add itself is the existing upAdd path via delegation. */
    upOpen(btn) {
      const row = btn.closest(D('upsell-row'));
      const sheet = row && q(row, D('upsell-sheet'));
      if (!sheet) return;
      sheet.hidden = !sheet.hidden;
      btn.setAttribute('aria-expanded', sheet.hidden ? 'false' : 'true');
      if (!sheet.hidden) this.upResolve(q(sheet, D('upsell-opt')) || btn);
    }

    upResolve(el) {
      const row = el.closest(D('upsell-row'));
      const sheet = row && q(row, D('upsell-sheet'));
      if (!sheet) return;
      const chosen = [];
      let complete = true;
      for (const group of sheet.querySelectorAll(D('upsell-opt-group'))) {
        const picked = q(group, D('upsell-opt') + ':checked');
        if (picked) chosen.push(picked.value);
        else complete = false;
      }
      const btn = q(sheet, D('upsell-add'));
      if (!btn) return;
      let variant = null;
      if (complete) {
        try {
          const map = JSON.parse(q(sheet, D('upsell-variants')).textContent);
          variant = map.find((v) => v.o.length === chosen.length && v.o.every((o, i) => o === chosen[i])) || null;
        } catch (e) { variant = null; }
      }
      const ready = !!(variant && variant.a);
      const soldout = !!(variant && !variant.a);
      btn.disabled = !ready;
      if (variant) btn.setAttribute(VID, variant.id);
      sheet.classList.toggle('is-ready', ready);
      sheet.classList.toggle('is-soldout', soldout);
      if (ready) {
        const priceEl = q(sheet, D('upsheet-price'));
        if (priceEl && priceEl.textContent !== variant.p) priceEl.textContent = variant.p;
      }
    }

    upAdd(btn) {
      if (btn._ryvtBusy) return;
      btn._ryvtBusy = true;
      const row = btn.closest(D('upsell-row'));
      if (row) row._ryvtKeep = true;
      btn.classList.remove('is-added');
      btn.classList.add('is-adding');
      btn.setAttribute('aria-busy', 'true');
      this.mutate('add', { items: [{ id: I(A(btn, VID)), quantity: 1 }] }, 'cart_upsell').then(() => {
        btn._ryvtBusy = false;
        btn.classList.remove('is-adding');
        btn.removeAttribute('aria-busy');
        btn.classList.add('is-added');
        this.say(A(btn, 'data-added-text') || '');
        setTimeout(() => {
          btn.classList.remove('is-added');
          if (row) row._ryvtKeep = false;
          this.upsells();
        }, 2000);
      }, (e) => {
        btn._ryvtBusy = false;
        if (row) row._ryvtKeep = false;
        btn.classList.remove('is-adding', 'is-added');
        btn.removeAttribute('aria-busy');
        this.upsells();
        if (!e || !e.stale) this.fail(e);
      });
    }

    changed(d) {
      this.syncCheckout();
      const alertEl = q(this, D('cart-alert'));
      if (alertEl) {
        alertEl.hidden = true;
        alertEl.textContent = '';
      }
      const html = d && d.cart && d.cart.sections && d.cart.sections['ryvt-cart-drawer'];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        this.meter(doc);
        const st = q(doc, TC);
        const dt = q(this, TC);
        if (st && dt) R.crossfadeText(dt, st.textContent.trim());
      }
      this.upsells();
      if (d && d.action === 'add' && !this.classList.contains('is-open')) this.open();
    }

    meter(doc) {
      const src = q(doc, D('meter'));
      const dst = q(this, D('meter'));
      if (!src || !dst) return;
      const sf = q(src, D('meter-fill'));
      const df = q(dst, D('meter-fill'));
      if (sf && df) df.style.setProperty('--p', sf.style.getPropertyValue('--p') || '0');
      const st = q(src, D('meter-track'));
      const dt = q(dst, D('meter-track'));
      if (st && dt) {
        for (const n of ['aria-valuemax', 'aria-valuenow', 'aria-valuetext']) {
          const v = A(st, n);
          if (v !== null) dt.setAttribute(n, v);
        }
      }
      for (const span of src.querySelectorAll('[' + MS + ']')) {
        const m = q(dst, '[' + MS + '="' + A(span, MS) + '"]');
        if (!m) continue;
        if (m.textContent !== span.textContent) m.textContent = span.textContent;
        m.classList.toggle('is-active', span.classList.contains('is-active'));
        if (span.hasAttribute('aria-hidden')) m.setAttribute('aria-hidden', 'true');
        else m.removeAttribute('aria-hidden');
      }
    }

    upsells() {
      const region = q(this, D('cart-upsell'));
      const data = q(this, D('cart-data'));
      if (!region || !data) return;
      const rows = [...region.querySelectorAll(D('upsell-row'))];
      const chosen = chooseMerch(region, data, this._vh.matches ? 1 : 2);
      let visible = 0;
      for (const r of rows) {
        const keep = chosen.indexOf(r) !== -1 || r._ryvtKeep === true;
        r.hidden = !keep;
        if (keep) visible += 1;
      }
      region.hidden = I(A(data, 'data-count')) === 0 || visible === 0;
    }

    refocus() {
      if (document.activeElement !== document.body) return;
      const t = q(this, TI);
      if (t && this.classList.contains('is-open')) t.focus();
    }

    say(text) {
      const live = q(this, D('cart-live'));
      if (live && text) live.textContent = text;
    }

    fail(e) {
      const el = q(this, D('cart-alert'));
      if (!el) return;
      el.textContent = (e && e.message) || A(el, 'data-error-text') || 'We could not update your bag.';
      el.hidden = false;
      el.focus({ preventScroll: true });
    }
  }

  customElements.define('ryvt-cart-drawer', RyvtCartDrawer);

  document.querySelectorAll(D('cart-recommendations')).forEach((region) => {
    const chosen = chooseMerch(region, region, 2);
    region.querySelectorAll(D('upsell-row')).forEach((row) => {
      row.hidden = chosen.indexOf(row) === -1;
    });
    region.hidden = chosen.length === 0;
  });
})();
